import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ViewIcon, ViewOffIcon } from '../../Icons';
import { getLocalSystemDate } from '../../utils/dateUtils';
import { BRAND_THEME } from '../../ui/brandTheme';
import DateInput from '../datePicker/DateInput';

const FormInput = ({
    label,
    register,
    name,
    error,
    type = "text",
    placeholder,
    icon: Icon,
    className = "",
    onInput,
    maxLength,
    required = false,
    size = "md",
    ...props
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === "password";
    let inputType = type;
    if (isPassword) {
        inputType = showPassword ? "text" : "password";
    }

    const today = getLocalSystemDate();
    const isCompact = size === "sm";
    const fieldClassName = `
            block w-full border bg-white text-slate-900 placeholder:text-slate-400
            transition-colors duration-200 ease-in-out outline-none
            disabled:bg-indigo-50/40 disabled:text-slate-500 disabled:cursor-not-allowed
            ${isCompact
                            ? "rounded-md text-sm py-1.5 shadow-none"
                            : "rounded-lg sm:text-sm py-2.5 shadow-sm"
                        }
            ${Icon ? (isCompact ? "pl-8" : "pl-10") : (isCompact ? "pl-2.5" : "pl-4")}
            ${isPassword ? (isCompact ? "pr-8" : "pr-10") : (isCompact ? "pr-2.5" : "pr-4")}
            ${error
                            ? `border-red-300 focus:border-red-400 placeholder-red-300 ${isCompact ? "focus:ring-1 focus:ring-red-100" : "focus:ring-2 focus:ring-red-100"}`
                            : `border-indigo-200 hover:border-indigo-300 ${BRAND_THEME.fieldFocus}`
                        }
          `;
    const fieldProps = {
        id: name,
        ...register(name),
        onInvalid: (e) => {
            e.preventDefault();
            e.stopPropagation();
        },
        max: type === "date" ? props.max || today : props.max,
        className: fieldClassName,
        placeholder,
        onInput,
        maxLength,
        ...props,
    };

    return (
        <div className={`${isCompact ? "space-y-1" : "space-y-1.5"} ${className}`}>
            <label
                htmlFor={name}
                className={`block font-medium text-slate-700 ${
                    isCompact ? "text-xs" : "text-sm font-semibold ml-1"
                }`}
            >
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative group">
                {Icon && (
                    <div className={`absolute inset-y-0 left-0 ${isCompact ? "pl-2.5" : "pl-3"} flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-500 transition-colors`}>
                        <Icon className={isCompact ? "w-4 h-4" : "w-5 h-5"} />
                    </div>
                )}
                {type === "date" ? (
                    <DateInput {...fieldProps} />
                ) : (
                    <input type={inputType} {...fieldProps} />
                )}

                {isPassword && (
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-indigo-600 focus:outline-none cursor-pointer z-10"
                        tabIndex="-1" // Prevent tab stop
                    >
                        {showPassword ? <ViewOffIcon className="w-5 h-5" /> : <ViewIcon className="w-5 h-5" />}
                    </button>
                )}

                {!isPassword && error && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    </div>
                )}
            </div>
            {error && (
                <p className="text-red-500 text-xs font-semibold ml-1 flex items-center gap-1 animate-fadeIn">
                    {error.message}
                </p>
            )}
        </div>
    );
};

FormInput.propTypes = {
    label: PropTypes.string.isRequired,
    register: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    error: PropTypes.object,
    type: PropTypes.string,
    placeholder: PropTypes.string,
    icon: PropTypes.elementType,
    className: PropTypes.string,
    size: PropTypes.oneOf(["sm", "md"]),
};

export default FormInput;
